import { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserPlus, Lock, Unlock, KeyRound, Pencil, Save, X, Trash2, Search } from "lucide-react";
import api, { formatErr } from "../../lib/api";

export default function DevUsers() {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", role: "admin", require_otp: false });
  const [editing, setEditing] = useState(null); // user being edited
  const [q, setQ] = useState("");

  const refresh = async () => {
    const [u, r] = await Promise.all([api.get("/developer/users"), api.get("/developer/roles")]);
    setUsers(u.data); setRoles(r.data);
  };
  useEffect(() => { refresh(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      const r = await api.post("/developer/users", form);
      toast.success(`Created. Temp password: ${r.data.temp_password}`);
      setCreating(false); setForm({ name: "", email: "", phone: "", password: "", role: "admin", require_otp: false }); refresh();
    } catch (err) { toast.error(formatErr(err)); }
  };

  const toggleLock = async (u) => {
    try { await api.put(`/developer/users/${u.id}`, { locked: !u.locked }); toast.success(u.locked ? "Unlocked" : "Locked"); refresh(); }
    catch (err) { toast.error(formatErr(err)); }
  };

  const resetPw = async (u) => {
    const pw = prompt("New password (min 8 chars):"); if (!pw) return;
    try { await api.put(`/developer/users/${u.id}`, { new_password: pw }); toast.success("Password updated"); }
    catch (err) { toast.error(formatErr(err)); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete user ${u.email}? This cannot be undone.`)) return;
    try { await api.delete(`/developer/users/${u.id}`); toast.success("User deleted"); refresh(); }
    catch (err) { toast.error(formatErr(err)); }
  };

  const saveEdit = async () => {
    try {
      const patch = {
        name: editing.name,
        email: editing.email,
        phone: editing.phone,
        role: editing.role,
        require_otp: editing.require_otp,
      };
      if (editing.new_password) patch.new_password = editing.new_password;
      await api.put(`/developer/users/${editing.id}`, patch);
      toast.success("User updated");
      setEditing(null); refresh();
    } catch (err) { toast.error(formatErr(err)); }
  };

  const filtered = users.filter((u) => {
    if (!q) return true;
    const t = q.toLowerCase();
    return (u.name || "").toLowerCase().includes(t) || (u.email || "").toLowerCase().includes(t) || (u.role || "").toLowerCase().includes(t);
  });

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div><div className="overline mb-2">Identity</div><h1 className="font-display text-4xl text-white">Users</h1></div>
        <button data-testid="user-new" onClick={() => setCreating(!creating)} className="btn-primary"><UserPlus size={14}/> New User</button>
      </div>

      <div className="relative max-w-md">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40"/>
        <input data-testid="users-search" className="input-box pl-9" placeholder="Search by name / email / role…" value={q} onChange={(e) => setQ(e.target.value)}/>
      </div>

      {creating && (
        <form onSubmit={create} className="card-luxe p-6 grid md:grid-cols-2 gap-4">
          <input data-testid="newuser-name" required placeholder="Name" className="input-box" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}/>
          <input data-testid="newuser-email" required type="email" placeholder="Email" className="input-box" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}/>
          <input data-testid="newuser-phone" placeholder="Phone" className="input-box" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}/>
          <input data-testid="newuser-pw" type="text" placeholder="Password (blank = auto)" className="input-box" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}/>
          <select data-testid="newuser-role" className="input-box" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
            {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
          <label className="flex items-center gap-2 text-sm text-white/70"><input type="checkbox" checked={form.require_otp} onChange={(e) => setForm({ ...form, require_otp: e.target.checked })}/> Require OTP on login</label>
          <button data-testid="newuser-save" className="btn-primary md:col-span-2 justify-center">Create User</button>
        </form>
      )}

      <div className="card-luxe overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-[11px] uppercase tracking-luxe text-white/40">
            <tr className="border-b border-white/5">
              <th className="text-left p-4">User</th><th className="text-left">Role</th><th className="text-left">Status</th><th className="text-left">Created</th><th className="text-right p-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((u) => (
              <tr key={u.id} className="border-b border-white/5" data-testid={`user-row-${u.id}`}>
                <td className="p-4"><div className="text-white">{u.name}</div><div className="text-white/40 text-xs">{u.email}</div></td>
                <td className="text-copper uppercase tracking-luxe text-xs">{u.role}</td>
                <td>{u.locked ? <span className="text-red-400 text-xs">● Locked</span> : <span className="text-[#32D74B] text-xs">● Active</span>}</td>
                <td className="text-white/50 text-xs">{u.created_at?.slice(0, 10)}</td>
                <td className="p-4 text-right">
                  <div className="inline-flex gap-2">
                    <button data-testid={`edit-${u.id}`} title="Edit user" onClick={() => setEditing({ ...u, new_password: "" })} className="btn-ghost text-xs"><Pencil size={13}/></button>
                    <button data-testid={`lock-${u.id}`} title={u.locked ? "Unlock" : "Lock"} onClick={() => toggleLock(u)} className="btn-ghost text-xs">{u.locked ? <Unlock size={13}/> : <Lock size={13}/>}</button>
                    <button data-testid={`pw-${u.id}`} title="Reset password" onClick={() => resetPw(u)} className="btn-ghost text-xs"><KeyRound size={13}/></button>
                    <button data-testid={`del-${u.id}`} title="Delete" onClick={() => remove(u)} className="text-xs px-2 py-1.5 text-red-400 hover:bg-red-500/10 border border-red-500/30 rounded-sm"><Trash2 size={13}/></button>
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan="5" className="p-8 text-center text-white/40">No users match your search</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <div data-testid="user-edit-modal" className="fixed inset-0 z-50 flex items-center justify-center p-4 maintenance-blur" onClick={() => setEditing(null)}>
          <div className="card-luxe p-6 max-w-2xl w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-5">
              <div>
                <div className="overline">Edit User</div>
                <div className="font-display text-2xl mt-1">{editing.name}</div>
                <div className="text-xs text-white/40 mt-0.5">ID: {editing.id}</div>
              </div>
              <button onClick={() => setEditing(null)} className="p-2 hover:text-copper" data-testid="user-edit-close"><X size={16}/></button>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <div className="text-[11px] uppercase tracking-luxe text-white/40 mb-1">Name</div>
                <input data-testid="user-edit-name" className="input-box" value={editing.name || ""} onChange={(e) => setEditing({ ...editing, name: e.target.value })}/>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-luxe text-white/40 mb-1">Email</div>
                <input data-testid="user-edit-email" type="email" className="input-box" value={editing.email || ""} onChange={(e) => setEditing({ ...editing, email: e.target.value })}/>
                <div className="text-[10px] text-white/40 mt-1">Changing email will allow the user to log in with the new address. They will be notified.</div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-luxe text-white/40 mb-1">Phone</div>
                <input data-testid="user-edit-phone" className="input-box" value={editing.phone || ""} onChange={(e) => setEditing({ ...editing, phone: e.target.value })}/>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-luxe text-white/40 mb-1">Role</div>
                <select data-testid="user-edit-role" className="input-box" value={editing.role || ""} onChange={(e) => setEditing({ ...editing, role: e.target.value })}>
                  {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select>
              </div>
              <div className="md:col-span-2">
                <div className="text-[11px] uppercase tracking-luxe text-white/40 mb-1">Reset Password (optional — leave blank to keep current)</div>
                <input data-testid="user-edit-password" type="text" className="input-box" placeholder="New password" value={editing.new_password || ""} onChange={(e) => setEditing({ ...editing, new_password: e.target.value })}/>
              </div>
              <label className="flex items-center gap-2 text-sm text-white/70 md:col-span-2">
                <input data-testid="user-edit-otp" type="checkbox" checked={!!editing.require_otp} onChange={(e) => setEditing({ ...editing, require_otp: e.target.checked })}/> Require OTP on login
              </label>
            </div>

            <div className="flex gap-3 mt-6">
              <button data-testid="user-edit-save" onClick={saveEdit} className="btn-primary"><Save size={13}/> Save Changes</button>
              <button onClick={() => setEditing(null)} className="btn-outline">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
